import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
} from '@mui/material';
import {
  Sync,
  EventBusy,
  Map,
  EmojiEvents,
  DirectionsCar,
  Cloud,
  Notifications,
  CalendarMonth,
  Leaderboard,
  Email,
  SportsSoccer,
  ArrowForward as ArrowForwardIcon,
  Check,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import Footer from '@/components/Footer';

const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';
const pill = { borderRadius: '999px', textTransform: 'none' as const, fontFamily: FONT, fontWeight: 600, px: 3.5 };

// ── CSS keyframes (injected once) ───────────────────────────────────
const STYLE_ID = 'features-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes feat-fade-up { from { opacity:0; transform:translateY(40px); } to { opacity:1; transform:translateY(0); } }
    @keyframes feat-fade-left { from { opacity:0; transform:translateX(40px); } to { opacity:1; transform:translateX(0); } }
    @keyframes feat-fade-right { from { opacity:0; transform:translateX(-40px); } to { opacity:1; transform:translateX(0); } }
    @keyframes feat-float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
    @keyframes feat-pulse { 0%,100% { opacity:0.6; } 50% { opacity:1; } }
    @keyframes feat-bar-grow { from { transform:scaleY(0); } to { transform:scaleY(1); } }
  `;
  document.head.appendChild(style);
}

// ── Scroll-triggered reveal ─────────────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

// ── Illustration components ─────────────────────────────────────────

const ScheduleGraphic: React.FC = () => {
  const days = Array.from({ length: 30 }, (_, i) => i + 1);
  const gameDays = [5, 8, 12, 19, 22, 26];
  return (
    <svg viewBox="0 0 380 300" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%' }}>
      {/* Calendar card - upper left */}
      <g>
        <rect x="10" y="10" width="260" height="220" rx="14" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
        <path d="M24 10H256a14 14 0 0 1 14 14v26H10V24a14 14 0 0 1 14-14z" fill="#f1f5f9" />
        <text x="140" y="34" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="12" fontWeight="700" fill="#334155">April 20XX</text>
        <line x1="10" y1="50" x2="270" y2="50" stroke="#e2e8f0" strokeWidth="1" />
        {/* Day headers */}
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <text key={`${d}${i}`} x={36 + i * 34} y="65" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="9" fontWeight="600" fill="#94a3b8">{d}</text>
        ))}
        {/* Day numbers */}
        {days.map((day) => {
          const cell = day + 2;
          const row = Math.floor(cell / 7);
          const col = cell % 7;
          const x = 36 + col * 34;
          const y = 86 + row * 28;
          const isGame = gameDays.includes(day);
          return (
            <g key={day}>
              {isGame && <circle cx={x} cy={y - 2} r="11" fill={`${ACCENT}18`} />}
              <text x={x} y={y + 3} textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="10" fontWeight={isGame ? '700' : '400'} fill={isGame ? ACCENT : '#64748b'}>{day}</text>
            </g>
          );
        })}
      </g>

      {/* Agenda card - overlapping on the right, lower */}
      <g>
        <rect x="152" y="62" width="220" height="230" rx="14" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
        <path d="M166 62H358a14 14 0 0 1 14 14v22H152V76a14 14 0 0 1 14-14z" fill="#f1f5f9" />
        <text x="180" y="83" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#334155">Upcoming</text>
        <line x1="152" y1="98" x2="372" y2="98" stroke="#e2e8f0" strokeWidth="1" />

        {/* Agenda items */}
        {[
          { day: 'Saturday', time: '9:00 AM', teams: 'Eagles vs Hawks', venue: 'Northside Field' },
          { day: 'Wednesday', time: '6:30 PM', teams: 'Storm vs Eagles', venue: 'Central Arena' },
          { day: 'Sunday', time: '10:00 AM', teams: 'Eagles vs Wolves', venue: 'Riverside Park' },
          { day: 'Sunday', time: '2:00 PM', teams: 'Lions vs Eagles', venue: 'Southside Park' },
        ].map((item, i) => (
          <g key={i}>
            <circle cx="170" cy={122 + i * 46} r="4" fill={ACCENT} />
            {i < 3 && <line x1="170" y1={126 + i * 46} x2="170" y2={164 + i * 46} stroke={`${ACCENT}30`} strokeWidth="1.5" />}
            <text x="184" fontFamily="Inter,sans-serif" fontSize="9" fontWeight="600" fill="#94a3b8" y={118 + i * 46}>{item.day}</text>
            <text x="184" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="600" fill="#0f172a" y={132 + i * 46}>{item.teams}</text>
            <text x="184" fontFamily="Inter,sans-serif" fontSize="9" fill="#94a3b8" y={144 + i * 46}>{item.time} · {item.venue}</text>
          </g>
        ))}
      </g>
    </svg>
  );
};

const ConflictGraphic: React.FC = () => (
  <svg viewBox="0 0 340 280" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%' }}>
    {/* Drawer card */}
    <rect x="20" y="10" width="300" height="260" rx="16" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
    {/* Header */}
    <text x="40" y="38" fontFamily="Inter,sans-serif" fontSize="14" fontWeight="700" fill="#0f172a">Schedule Conflicts</text>
    <text x="40" y="56" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="500" fill="#64748b">Conflicts for Jake's 9:00 AM match</text>
    <text x="40" y="72" fontFamily="Inter,sans-serif" fontSize="11" fill="#94a3b8">v Hawks United</text>
    <line x1="30" y1="82" x2="310" y2="82" stroke="#f1f5f9" strokeWidth="1" />

    {/* Conflict card 1 - DIRECT (red) */}
    <g style={{}}>
      {/* Left severity border */}
      <rect x="36" y="94" width="268" height="76" rx="10" fill="white" stroke="#fecaca" strokeWidth="0.5" />
      {/* Direction ribbon */}
      <path d="M46 94H294a10 10 0 0 1 10 10v14H36v-14a10 10 0 0 1 10-10z" fill="rgba(220,38,38,0.06)" />
      <rect x="283" y="94" width="21" height="24" rx="10" fill="transparent" />
      {/* Arrow icon */}
      <path d="M50 106l5-4v3h8v2h-8v3z" fill="#dc2626" />
      <text x="68" y="110" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="500" fill="#dc2626">9:30 AM</text>
      <text x="108" y="110" fontFamily="Inter,sans-serif" fontSize="11" fill="#dc2626" opacity="0.6">·</text>
      <text x="116" y="110" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="500" fill="#dc2626">Emma</text>
      {/* Body */}
      <text x="48" y="136" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="500" fill="#dc2626">Overlapping matches</text>
      <text x="48" y="152" fontFamily="Inter,sans-serif" fontSize="11" fill="#334155">Wolves vs Lions</text>
      <text x="48" y="165" fontFamily="Inter,sans-serif" fontSize="10" fill="#94a3b8">Southside Park</text>
    </g>

    {/* Conflict card 2 - MODERATE (amber) */}
    <g style={{}}>
      <rect x="36" y="182" width="268" height="76" rx="10" fill="white" stroke="#fde68a" strokeWidth="0.5" />
      {/* Direction ribbon */}
      <path d="M46 182H294a10 10 0 0 1 10 10v14H36v-14a10 10 0 0 1 10-10z" fill="rgba(217,119,6,0.06)" />
      <rect x="283" y="182" width="21" height="24" rx="10" fill="transparent" />
      {/* Arrow icon */}
      <path d="M56 194l-5-4v3h-8v2h8v3z" fill="#d97706" />
      <text x="68" y="198" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="500" fill="#d97706">11:00 AM</text>
      {/* Body */}
      <text x="48" y="224" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="500" fill="#d97706">Back-to-back, 22 min drive</text>
      <text x="48" y="240" fontFamily="Inter,sans-serif" fontSize="11" fill="#334155">Storm vs Thunder</text>
      <text x="48" y="253" fontFamily="Inter,sans-serif" fontSize="10" fill="#94a3b8">Central Arena · 12.4 mi</text>
    </g>
  </svg>
);

const TravelMapBackground: React.FC = () => (
  <Box sx={{
    position: 'absolute', inset: 0, zIndex: 0,
    display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
    opacity: 0.45, filter: 'saturate(0.4)',
  }}>
    {[
      [2100, 3050], [2101, 3050],
      [2100, 3051], [2101, 3051],
    ].map(([x, y]) => (
      <Box
        key={`${x}-${y}`}
        component="img"
        src={`https://tile.openstreetmap.org/13/${x}/${y}.png`}
        alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    ))}
  </Box>
);

const TravelGraphic: React.FC = () => (
  <svg viewBox="0 0 340 260" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'relative', width: '100%', display: 'block', zIndex: 1 }}>
    {/* Route arch */}
    <path d="M50 220 Q140 95 300 50" stroke="white" strokeWidth="4" fill="none" opacity="0.5" />
    <path d="M50 220 Q140 95 300 50" stroke={ACCENT} strokeWidth="2.5" fill="none" />
    {/* Origin pin */}
    <circle cx="50" cy="220" r="12" fill={ACCENT} />
    <circle cx="50" cy="220" r="5" fill="white" />
    <text x="30" y="248" fontFamily="Inter,sans-serif" fontSize="11" fill="#0f172a" fontWeight="700">Home</text>
    {/* Destination pin */}
    <circle cx="300" cy="50" r="12" fill="#ef4444" />
    <circle cx="300" cy="50" r="5" fill="white" />
    <text x="278" y="38" fontFamily="Inter,sans-serif" fontSize="11" fill="#0f172a" fontWeight="700">Field #4</text>
    {/* Travel info card */}
    <g style={{}}>
      <rect x="105" y="105" width="140" height="56" rx="14" fill="white" stroke="#e2e8f0" strokeWidth="1" />
      <text x="175" y="129" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="14" fontWeight="700" fill="#0f172a">32 min drive</text>
      <text x="175" y="149" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="12" fill="#64748b">Leave by 8:28 AM</text>
    </g>
    {/* Weather card */}
    <g style={{}}>
      <rect x="135" y="169" width="80" height="36" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1" />
      <circle cx="157" cy="187" r="7" fill="#fbbf24" opacity="0.8" />
      <ellipse cx="162" cy="190" rx="8" ry="5" fill="#e2e8f0" />
      <text x="177" y="192" fontFamily="Inter,sans-serif" fontSize="13" fontWeight="600" fill="#334155">62°F</text>
    </g>
  </svg>
);

const AnalyticsGraphic: React.FC = () => (
  <svg viewBox="0 0 340 260" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%' }}>
    {/* Card */}
    <rect x="20" y="10" width="300" height="240" rx="14" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
    {/* Header - rounded top only */}
    <path d="M34 10H306a14 14 0 0 1 14 14v30H20V24a14 14 0 0 1 14-14z" fill="#f8fafc" />
    <text x="45" y="37" fontFamily="Inter,sans-serif" fontSize="13" fontWeight="800" fill="#334155">League Standings</text>
    <line x1="20" y1="54" x2="320" y2="54" stroke="#e2e8f0" strokeWidth="1" />
    {/* Column headers */}
    <text x="45" y="76" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#94a3b8">#</text>
    <text x="68" y="76" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#94a3b8">Team</text>
    <text x="195" y="76" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#94a3b8">Record</text>
    <text x="272" y="76" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#94a3b8">Pts</text>
    <line x1="30" y1="84" x2="310" y2="84" stroke="#f1f5f9" strokeWidth="1" />
    {/* Rows */}
    {[
      { rank: '1', team: 'Eagles FC', record: '8-1-1', pts: '25', hl: true },
      { rank: '2', team: 'Storm SC', record: '7-1-2', pts: '22', hl: false },
      { rank: '3', team: 'Hawks United', record: '6-2-2', pts: '20', hl: false },
      { rank: '4', team: 'Thunder FC', record: '5-2-3', pts: '17', hl: false },
      { rank: '5', team: 'Wolves AC', record: '4-2-4', pts: '14', hl: false },
    ].map((row, i) => {
      const y = 108 + i * 32;
      return (
        <g key={i}>
          {row.hl && <rect x="28" y={y - 18} width="284" height="30" rx="6" fill={`${ACCENT}0a`} />}
          <text x="45" y={y} fontFamily="Inter,sans-serif" fontSize="12" fontWeight="600" fill={row.hl ? ACCENT : '#94a3b8'}>{row.rank}</text>
          <text x="68" y={y} fontFamily="Inter,sans-serif" fontSize="12" fontWeight={row.hl ? '700' : '500'} fill={row.hl ? '#0f172a' : '#475569'}>{row.team}</text>
          <text x="195" y={y} fontFamily="Inter,sans-serif" fontSize="12" fill="#64748b">{row.record}</text>
          <text x="272" y={y} fontFamily="Inter,sans-serif" fontSize="12" fontWeight="700" fill={row.hl ? ACCENT : '#475569'}>{row.pts}</text>
          {i < 4 && <line x1="30" y1={y + 8} x2="310" y2={y + 8} stroke="#f8fafc" strokeWidth="1" />}
        </g>
      );
    })}
  </svg>
);

const NotificationsGraphic: React.FC = () => (
  <svg viewBox="0 0 340 260" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%' }}>
    {/* Phone */}
    <rect x="60" y="6" width="220" height="248" rx="24" fill="white" stroke="#e2e8f0" strokeWidth="2" />
    <rect x="140" y="16" width="60" height="7" rx="3.5" fill="#e2e8f0" />
    <line x1="60" y1="36" x2="280" y2="36" stroke="#f1f5f9" strokeWidth="1" />
    {/* Notification 1 */}
    <g style={{}}>
      <rect x="76" y="48" width="188" height="52" rx="12" fill={`${ACCENT}0c`} stroke={ACCENT} strokeWidth="1" />
      <circle cx="96" cy="66" r="8" fill={ACCENT} />
      <text x="96" y="70" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="10" fill="white" fontWeight="700">!</text>
      <text x="114" y="68" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#0f172a">Schedule Change</text>
      <text x="114" y="86" fontFamily="Inter,sans-serif" fontSize="10" fill="#64748b">Game moved to 3:00 PM</text>
    </g>
    {/* Notification 2 */}
    <g style={{}}>
      <rect x="76" y="110" width="188" height="52" rx="12" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
      <circle cx="96" cy="128" r="8" fill="#3b82f6" />
      <path d="M92 128l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="114" y="130" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#0f172a">Result Posted</text>
      <text x="114" y="148" fontFamily="Inter,sans-serif" fontSize="10" fill="#64748b">Storm 3 - Hawks 1</text>
    </g>
    {/* Notification 3 */}
    <g style={{}}>
      <rect x="76" y="172" width="188" height="52" rx="12" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
      <circle cx="96" cy="190" r="8" fill="#f59e0b" />
      <path d="M93 190h6M96 187v6" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
      <text x="114" y="192" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fill="#0f172a">Weekly Digest</text>
      <text x="114" y="210" fontFamily="Inter,sans-serif" fontSize="10" fill="#64748b">4 games this week</text>
    </g>
  </svg>
);

// ── Feature sections ────────────────────────────────────────────────

interface FeatureSection {
  title: string;
  subtitle: string;
  points: string[];
  graphic: React.FC;
  icons: typeof Sync[];
  mapBackground?: React.FC;
}

const sections: FeatureSection[] = [
  {
    title: 'Schedules that stay in sync',
    subtitle: 'Connect your leagues once and never copy a schedule again.',
    points: [
      'Link MLS Next, ECNL, GA, NPL, State Leagues, and more',
      'Automatic syncing keeps every game up to date',
      'Unified calendar across all players and teams',
    ],
    graphic: ScheduleGraphic,
    icons: [Sync, CalendarMonth],
  },
  {
    title: 'Know about conflicts before they happen',
    subtitle: 'Player Central watches for overlapping games so you can plan ahead.',
    points: [
      'Detects time conflicts across all your leagues',
      'Factors in real drive times between venues',
      'Flags back-to-back games that are not actually possible',
    ],
    graphic: ConflictGraphic,
    icons: [EventBusy, DirectionsCar],
  },
  {
    title: 'Travel, weather, and directions at a glance',
    subtitle: 'Everything you need to get to the right field at the right time.',
    points: [
      'Drive time estimates and departure times',
      'Get directions to every venue',
      'Weather forecast for every match so you can pack smart',
    ],
    graphic: TravelGraphic,
    icons: [Map, Cloud],
    mapBackground: TravelMapBackground,
  },
  {
    title: 'Standings, rankings, and analysis',
    subtitle: 'Understand where your team stands without digging through league sites.',
    points: [
      'Live standings updated after every result',
      'Power rankings that go beyond win-loss records',
      'Position analysis to see how your lineup stacks up',
    ],
    graphic: AnalyticsGraphic,
    icons: [Leaderboard, EmojiEvents, SportsSoccer],
  },
  {
    title: 'Notifications and digests',
    subtitle: 'Stay informed without having to check constantly.',
    points: [
      'Push notifications for schedule changes and results',
      'Weekly email digests with upcoming games and standings',
      'Never miss a change or a result again',
    ],
    graphic: NotificationsGraphic,
    icons: [Notifications, Email],
  },
];

// ── Page component ──────────────────────────────────────────────────

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await api.get('/auth/signup-status');
        setSignupsEnabled(r.data.signupsEnabled);
        setWaitlistEnabled(r.data.waitlistEnabled ?? false);
      } catch {
        setSignupsEnabled(true);
        setWaitlistEnabled(false);
      }
    };
    check();
  }, []);

  const handleGetStarted = () => {
    if (signupsEnabled) navigate('/register');
  };

  return (
    <Box sx={{ bgcolor: 'white', fontFamily: FONT }}>
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 50% 30%, rgba(16,185,129,0.06) 0%, transparent 60%),
            linear-gradient(to bottom, #fafbfc 0%, white 100%)
          `,
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ opacity: 0, animation: 'feat-fade-up 0.7s 0s ease both' }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: FONT, fontWeight: 800,
                fontSize: { xs: '2.2rem', md: '3rem' },
                lineHeight: 1.1, letterSpacing: '-0.03em',
                color: '#0f172a', mb: 2,
              }}
            >
              Everything your family needs this season
            </Typography>
          </Box>
          <Box sx={{ opacity: 0, animation: 'feat-fade-up 0.7s 0.15s ease both' }}>
            <Typography sx={{
              fontFamily: FONT, fontSize: { xs: '1.05rem', md: '1.2rem' },
              color: 'text.secondary', lineHeight: 1.6, maxWidth: 560, mx: 'auto',
            }}>
              From schedule syncing to league analytics, Player Central keeps busy sports families organized and informed.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ─── Feature sections (alternating) ─────────────────────── */}
      {sections.map((section, idx) => {
        const even = idx % 2 === 0;
        return <FeatureRow key={section.title} section={section} reversed={!even} index={idx} />;
      })}

      {/* ─── CTA ────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#0f172a' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography sx={{
            fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
            fontSize: { xs: '1.8rem', md: '2.4rem' }, color: 'white', mb: 2,
          }}>
            Ready to get started?
          </Typography>
          <Typography sx={{
            fontFamily: FONT, fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, mb: 5,
          }}>
            All features are free during early access. No credit card required.
          </Typography>
          <Button
            size="large"
            variant="contained"
            endIcon={signupsEnabled ? <ArrowForwardIcon /> : undefined}
            sx={{
              ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK },
              boxShadow: `0 4px 14px ${ACCENT}40`, py: 1.5, px: 4, fontSize: '1rem',
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)' },
            }}
            onClick={handleGetStarted}
            disabled={!signupsEnabled && !waitlistEnabled}
          >
            {signupsEnabled ? 'Get started free' : waitlistEnabled ? 'Join the waitlist' : 'Coming soon'}
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

// ── Single feature row ──────────────────────────────────────────────

const FeatureRow: React.FC<{ section: FeatureSection; reversed: boolean; index: number }> = ({ section, reversed, index }) => {
  const { ref, visible } = useReveal();
  const Graphic = section.graphic;
  const MapBg = section.mapBackground;
  const isEvenSection = index % 2 === 0;

  const textContent = (
    <Box sx={{
      opacity: 0,
      ...(visible && {
        animation: `${reversed ? 'feat-fade-right' : 'feat-fade-left'} 0.7s 0.1s ease both`,
      }),
    }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {section.icons.map((Icon, i) => (
          <Box key={i} sx={{
            width: 36, height: 36, borderRadius: '8px', bgcolor: `${ACCENT}12`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon sx={{ fontSize: 18, color: ACCENT }} />
          </Box>
        ))}
      </Box>
      <Typography sx={{
        fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
        fontSize: { xs: '1.5rem', md: '2rem' }, color: '#0f172a', mb: 1.5, lineHeight: 1.15,
      }}>
        {section.title}
      </Typography>
      <Typography sx={{
        fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.6, mb: 3,
      }}>
        {section.subtitle}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {section.points.map((point, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: 99, flexShrink: 0,
              bgcolor: `${ACCENT}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mt: 0.2,
            }}>
              <Check sx={{ fontSize: 14, color: ACCENT }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: '#334155', lineHeight: 1.5 }}>
              {point}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const graphicContent = (
    <Box sx={{
      opacity: 0,
      ...(visible && {
        animation: `${reversed ? 'feat-fade-left' : 'feat-fade-right'} 0.7s 0.25s ease both`,
      }),
      bgcolor: MapBg ? 'transparent' : (isEvenSection ? '#f8fafc' : 'white'),
      borderRadius: '16px',
      border: MapBg ? 'none' : '1px solid rgba(0,0,0,0.06)',
      p: { xs: 3, md: 4 },
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {MapBg && <MapBg />}
      <Box sx={{ width: '100%', maxWidth: 360, position: 'relative', zIndex: 1 }}>
        <Graphic />
      </Box>
    </Box>
  );

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: isEvenSection ? 'white' : '#fafbfc',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 5, md: 8 },
          alignItems: 'center',
        }}>
          {reversed ? (
            <>
              <Box sx={{ order: { xs: 1, md: 0 } }}>{graphicContent}</Box>
              <Box sx={{ order: { xs: 0, md: 1 } }}>{textContent}</Box>
            </>
          ) : (
            <>
              {textContent}
              {graphicContent}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesPage;
